// src/composables/useConsoleCapture.js
//
// Lightweight in-memory console ring buffer for bug-report auto-capture.
// Patches console.log/info/warn/error once at app startup (see main.js) and
// re-forwards to the native console so normal behavior is preserved.
//
// The buffer holds the most recent N messages (default 150). snapshot()
// returns a serializable copy for uploading alongside a bug report.
//
// We also hook window.onerror + unhandledrejection so red errors that don't
// go through console (framework-thrown, async rejections) still show up.

const BUFFER_LIMIT = 150
const LEVELS = ['log', 'info', 'warn', 'error']

const buffer = []
let installed = false

function stringifyArg(arg) {
  if (arg == null) return String(arg)
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`
  }
  try {
    return JSON.stringify(arg, (_, v) => {
      if (typeof v === 'function') return `[function ${v.name || 'anonymous'}]`
      if (typeof v === 'bigint') return v.toString()
      return v
    })
  } catch {
    try {
      return String(arg)
    } catch {
      return '[unserializable]'
    }
  }
}

function push(level, args, source) {
  const entry = {
    level,
    timestamp: new Date().toISOString(),
    message: args.map(stringifyArg).join(' '),
  }
  if (source) entry.source = source
  buffer.push(entry)
  if (buffer.length > BUFFER_LIMIT) buffer.splice(0, buffer.length - BUFFER_LIMIT)
}

export function installConsoleCapture() {
  if (installed || typeof window === 'undefined') return
  installed = true

  const native = {}
  for (const level of LEVELS) {
    native[level] = console[level]?.bind(console) || (() => {})
    console[level] = (...args) => {
      try {
        push(level, args)
      } catch {
        /* never break console */
      }
      try {
        native[level](...args)
      } catch {
        /* ignore */
      }
    }
  }

  window.addEventListener('error', event => {
    try {
      const msg = event?.error?.stack || event?.message || 'Unknown error'
      const src = event?.filename ? `${event.filename}:${event.lineno}:${event.colno}` : null
      push('error', [msg], src)
    } catch {
      /* ignore */
    }
  })

  window.addEventListener('unhandledrejection', event => {
    try {
      const reason = event?.reason
      const msg =
        reason instanceof Error
          ? `${reason.name}: ${reason.message}${reason.stack ? `\n${reason.stack}` : ''}`
          : stringifyArg(reason)
      push('error', [`Unhandled rejection: ${msg}`])
    } catch {
      /* ignore */
    }
  })
}

export function snapshotConsole() {
  return buffer.slice()
}

export function clearConsoleCapture() {
  buffer.length = 0
}
